import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Container } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { accessApi } from '../../access/api.ts';
import { createAccessCeremony } from '../../access/ceremony.ts';
import { roomTypeLabel } from '../../components/threadRoomType.ts';
import './AccessCenter.scss';

type OpenedThread = {
  id: number;
  name: string;
  roomType: string;
  correspondents?: Array<{ label: string; isTarget: boolean }>;
  messages: Array<{ id: number; time: string; type: string; text: string; senderLabel?: string }>;
};

type Omission = { threadId: number | null; roomType: string; reason: string };

function omissionLabel(row: Omission) {
  if (row.reason === 'school-classmate-never-in-corpus' || row.reason === 'school-chat-never-in-corpus') {
    return 'School classmate chat omitted';
  }
  if (row.reason === 'school-staff-cannot-minimise-shared-key') {
    return 'School staff thread omitted (shared key cannot strip a child’s words)';
  }
  if (row.reason === 'outside-signed-order-scope') {
    return `${roomTypeLabel(row.roomType)} thread omitted: not in the signed order`;
  }
  return `${roomTypeLabel(row.roomType)} omitted`;
}

function remainingLabel(expiresAt: string) {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const minutes = Math.max(0, Math.floor(ms / 60000));
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m remaining`;
}

export default function AccessSessionShell() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<{
    caseReference: string;
    target: string;
    window: string;
    expiresAt: string;
    orderHash: string;
    sessionFingerprint: string;
    releases: string;
    matched?: boolean;
  } | null>(null);
  const [threads, setThreads] = useState<OpenedThread[]>([]);
  const [limitations, setLimitations] = useState<string[]>([]);
  const [omissions, setOmissions] = useState<Omission[]>([]);
  const [expired, setExpired] = useState(false);

  const closeAndWipe = useCallback(async () => {
    if (!sessionId) return;
    const ceremony = createAccessCeremony();
    try {
      await accessApi.close(sessionId);
    } catch {
      // Session may already be closed by expiry.
    }
    await ceremony.destroySessionKey(sessionId);
    setThreads([]);
    setLimitations([]);
    setOmissions([]);
    setExpired(true);
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const ceremony = createAccessCeremony();
    (async () => {
      try {
        const opened = await ceremony.openBundle(sessionId);
        if (cancelled) return;
        const expiresAt = opened.view.session.expiresAt;
        setBanner({
          caseReference: opened.view.session.caseReference || opened.view.order.caseReference || opened.view.order.id,
          target: opened.view.target.fullName || opened.view.target.shortName || opened.view.order.targetUserId,
          window: `${opened.view.order.periodStart} → ${opened.view.order.periodEnd}`,
          expiresAt,
          orderHash: opened.view.order.orderHash,
          sessionFingerprint: opened.view.signedOrder.payload.sessionPublicKeyFingerprint,
          releases: opened.view.releases.map((row) => row.role).join(', ') || 'none',
          matched: opened.view.attestations[0]?.matchedScope
        });
        setThreads(opened.threads);
        setLimitations(opened.limitations || []);
        setOmissions(opened.omissions || []);
        if (new Date(expiresAt).getTime() <= Date.now()) {
          await closeAndWipe();
        }
      } catch (cause) {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : 'Could not open the investigator session.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [closeAndWipe, sessionId]);

  useEffect(() => {
    if (!banner?.expiresAt) return undefined;
    const timer = window.setInterval(() => {
      if (new Date(banner.expiresAt).getTime() <= Date.now()) {
        void closeAndWipe();
      } else {
        setBanner((current) => (current ? { ...current } : current));
      }
    }, 15000);
    return () => window.clearInterval(timer);
  }, [banner?.expiresAt, closeAndWipe]);

  return (
    <div className="access-session-shell">
      <Container>
        <div className="access-banner">
          <h1 className="h4">Investigator viewing shell</h1>
          {banner && (
            <dl>
              <dt>Case</dt>
              <dd>{banner.caseReference}</dd>
              <dt>Target</dt>
              <dd>{banner.target}</dd>
              <dt>Window</dt>
              <dd>{banner.window}</dd>
              <dt>Time left</dt>
              <dd>{remainingLabel(banner.expiresAt)}</dd>
              <dt>Order hash</dt>
              <dd><code>{banner.orderHash}</code></dd>
              <dt>Session fingerprint</dt>
              <dd><code>{banner.sessionFingerprint}</code></dd>
              <dt>Custodians released</dt>
              <dd>{banner.releases}</dd>
              <dt>Witness</dt>
              <dd>
                {banner.matched == null ? 'Not yet attested' : banner.matched ? 'Scope matched' : 'Flagged'}
              </dd>
            </dl>
          )}
          <p className="mb-2">
            Only threads in the signed order. Family only if that order lists family. School classmate and school-staff
            rooms are omitted. No settings, recovery, export, or adding people. Havn tingrett and the named custodians
            are not real counterparties.
          </p>
          {limitations.map((line) => (
            <Alert key={line} variant="warning" className="py-2">
              {line}
            </Alert>
          ))}
          <Button variant="outline-danger" onClick={() => closeAndWipe()}>Close session and destroy keys</Button>
        </div>
        {error && <Alert variant="danger">{error}</Alert>}
        {expired && (
          <Alert variant="warning">
            The session is closed. Workstation keys for this case were destroyed.
            <Button variant="link" onClick={() => navigate('/access')}>Back to the drill</Button>
          </Alert>
        )}
        {!expired && omissions.length > 0 && (
          <section className="access-omissions">
            <h2 className="h5">Omitted from this bundle</h2>
            <ul>
              {omissions.map((row) => (
                <li key={`${row.roomType}-${row.threadId}-${row.reason}`}>{omissionLabel(row)}</li>
              ))}
            </ul>
          </section>
        )}
        {!expired && threads.map((thread) => (
          <section key={thread.id} className="access-thread">
            <h2 className="h5">{thread.name}</h2>
            <p className="text-body-secondary mb-1">{roomTypeLabel(thread.roomType)}</p>
            {thread.correspondents && thread.correspondents.length > 0 && (
              <p className="text-body-secondary small">
                {thread.correspondents.map((row) => row.label).join(', ')}
              </p>
            )}
            {thread.messages.length === 0 && <p>No stored messages in this window.</p>}
            {thread.messages.map((message) => (
              <div key={message.id} className="access-message">
                <div className="text-body-secondary small">
                  {message.senderLabel || 'Correspondent'} · {message.time}
                </div>
                <div>{message.text}</div>
              </div>
            ))}
          </section>
        ))}
      </Container>
    </div>
  );
}
